<script setup lang="ts">
import { computed, ref } from 'vue';
import { Link, router, usePage } from '@inertiajs/vue3';
import { useI18n } from 'vue-i18n';

const { t, locale: i18nLocale } = useI18n();
import {
    House,
    Globe,
    Layers,
    Users,
    Radio,
    PenLine,
    Mail,
    Inbox,
    Landmark,
    Settings,
    CircleHelp,
    CirclePlay,
    AlertTriangle,
    X,
    LayoutGrid,
    HandCoins,
    UserCheck,
    ScrollText,
    CreditCard,
    Package,
    FileText,
    Newspaper,
    Code,
    Sun,
    Moon,
    Monitor,
    ShoppingBag,
} from 'lucide-vue-next';
import AppLogoIcon from '@/components/AppLogoIcon.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
} from '@/components/ui/sidebar';
import { useAppearance } from '@/composables/useAppearance';
import { setLocale, type Locale } from '@/i18n';
import { dashboard } from '@/routes';
import { dashboard as adminDashboard, creators as adminCreators, members as adminMembers, paymentOps as adminPaymentOps, audits as adminAudits, apiManagement as adminApiManagement, settings as adminSettings, packages as adminPackages, landingPages as adminLandingPages, newsletters as adminNewsletters } from '@/routes/admin';
import { dashboard as creatorDashboard, inbox as creatorInbox, members as creatorMembers, contributions as creatorContributions, payouts as creatorPayouts } from '@/routes/creator';
import { complete as completeTourRoute } from '@/routes/creator/onboarding-tour';
import { index as creatorCampaigns } from '@/routes/creator/campaigns';
import { index as creatorPlans } from '@/routes/creator/plans';
import { index as creatorCommunity } from '@/routes/creator/community';
import { index as creatorPosts } from '@/routes/creator/posts';
import { index as creatorBundles } from '@/routes/creator/bundles';
import { edit as creatorProfile } from '@/routes/creator/profile';
import { overview as memberOverview, payments as memberPayments, purchases as memberPurchases, inbox as memberInbox } from '@/routes/member';
import { useCurrentUrl } from '@/composables/useCurrentUrl';
import type { Auth, UserRole } from '@/types';

type CreatorNav = {
    displayName: string;
    slug: string;
    isPaymentConnected: boolean;
    paymentProvider: string | null;
    supporterCount: number;
    onboardingTourCompleted: boolean;
} | null;

const page = usePage<{ auth: Auth; creatorNav: CreatorNav; campaignsEnabled: boolean; inboxUnread: number }>();
const role = computed<UserRole>(() => page.props.auth.user.role);
const user = computed(() => page.props.auth.user);
const creatorNav = computed(() => page.props.creatorNav);
const campaignsEnabled = computed(() => page.props.campaignsEnabled !== false);
const inboxUnread = computed(() => page.props.inboxUnread ?? 0);
const isOwner = computed(() => page.props.auth.user.organization_role !== 'member');

const { isCurrentUrl } = useCurrentUrl();

type SidebarNavItem = {
    id: string;
    label: string;
    href: string;
    icon?: typeof House;
    image?: string;
    count?: number;
    external?: boolean;
};

const primaryNav = computed<SidebarNavItem[]>(() => {
    if (role.value === 'creator') {
        const items: SidebarNavItem[] = [
            { id: 'dashboard', label: t('backend.sidebar.dashboard'), href: creatorDashboard.url(), icon: House },
        ];

        if (isOwner.value) {
            items.push(
                { id: 'page', label: t('backend.sidebar.myPage'), href: creatorProfile.url(), icon: Globe },
                { id: 'plans', label: t('backend.sidebar.plans'), href: creatorPlans.url(), icon: Layers },
            );
        }

        items.push(
            { id: 'supporters', label: t('backend.sidebar.supporters'), href: creatorMembers.url(), icon: Users, count: creatorNav.value?.supporterCount },
            { id: 'contributions', label: t('backend.sidebar.contributions'), href: creatorContributions.url(), icon: HandCoins },
        );

        if (isOwner.value) {
            items.push(
                { id: 'community', label: t('backend.sidebar.community'), href: creatorCommunity.url(), icon: Radio },
                { id: 'posts', label: t('backend.sidebar.posts'), href: creatorPosts.url(), icon: PenLine },
                { id: 'bundles', label: t('backend.sidebar.bundles'), href: creatorBundles.url(), icon: Package },
            );
        }

        items.push({ id: 'inbox', label: t('backend.sidebar.inbox'), href: creatorInbox.url(), icon: Inbox, count: inboxUnread.value || undefined });

        if (campaignsEnabled.value) {
            items.push({ id: 'campaigns', label: t('backend.sidebar.campaigns'), href: creatorCampaigns.url(), icon: Mail });
        }

        if (isOwner.value) {
            items.push({ id: 'payouts', label: t('backend.sidebar.payouts'), href: creatorPayouts.url(), icon: Landmark });
        }

        return items;
    }
    if (role.value === 'admin') {
        return [
            { id: 'overview', label: t('backend.admin.overview'), href: adminDashboard.url(), icon: House },
            { id: 'creators', label: t('backend.admin.creatorsNav'), href: adminCreators.url(), icon: Users },
            { id: 'members', label: 'Members', href: adminMembers.url(), icon: UserCheck },
            { id: 'payment-ops', label: t('backend.admin.paymentOps'), href: adminPaymentOps.url(), icon: CreditCard },
            { id: 'audits', label: t('backend.admin.auditsNav'), href: adminAudits.url(), icon: ScrollText },
            { id: 'api-management', label: 'API Management', href: adminApiManagement.url(), icon: Code },
            { id: 'packages', label: t('backend.admin.packagesNav'), href: adminPackages.url(), icon: Package },
            { id: 'landing-pages', label: t('backend.admin.landingPagesNav'), href: adminLandingPages.url(), icon: FileText },
            { id: 'newsletters', label: 'Newsletters', href: adminNewsletters.url(), icon: Newspaper },
            { id: 'settings', label: t('backend.admin.settingsNav'), href: adminSettings.url(), icon: Settings },
        ];
    }
    return [
        { id: 'overview', label: t('backend.member.mySupports'), href: memberOverview.url(), icon: Users },
        { id: 'purchases', label: t('backend.member.purchases'), href: memberPurchases.url(), icon: ShoppingBag },
        { id: 'payments', label: t('backend.member.payments'), href: memberPayments.url(), icon: HandCoins },
        { id: 'inbox', label: t('backend.sidebar.inbox'), href: memberInbox.url(), icon: Inbox, count: inboxUnread.value || undefined },
    ];
});

const secondaryNav = computed<SidebarNavItem[]>(() => {
    const items: SidebarNavItem[] = [];
    if (role.value === 'user') {
        items.push({ id: 'settings', label: t('backend.sidebar.settings'), href: '/app/settings/profile', icon: Settings });
    }
    items.push({ id: 'help', label: t('backend.sidebar.help'), href: 'https://help.novantt.com', icon: CircleHelp, external: true });
    return items;
});

function getInitials(name: string): string {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

// Theme
const { appearance, updateAppearance } = useAppearance();

const themes = [
    { value: 'light' as const, icon: Sun, label: computed(() => t('setup.themeLight')) },
    { value: 'dark' as const, icon: Moon, label: computed(() => t('setup.themeDark')) },
    { value: 'system' as const, icon: Monitor, label: computed(() => t('setup.themeSystem')) },
];

// Tour dismiss
const showTourDismissConfirm = ref(false);
const tourDismissed = ref(false);

const showTourButton = computed(() => {
    if (role.value !== 'creator') return false;
    if (tourDismissed.value) return false;
    return !creatorNav.value?.onboardingTourCompleted;
});

function confirmDismissTour() {
    showTourDismissConfirm.value = false;
    tourDismissed.value = true;
    router.post(completeTourRoute().url, {}, {
        preserveState: true,
        preserveScroll: true,
    });
}

// Language
const locales = [
    { value: 'en' as Locale, label: 'English', flag: '🇬🇧' },
    { value: 'nl' as Locale, label: 'Nederlands', flag: '🇳🇱' },
    { value: 'de' as Locale, label: 'Deutsch', flag: '🇩🇪' },
];

const currentLocaleData = computed(() => locales.find(l => l.value === i18nLocale.value) ?? locales[0]);
const showLangMenu = ref(false);

function changeLocale(locale: Locale) {
    setLocale(locale);
    showLangMenu.value = false;
}
</script>

<template>
    <Sidebar class="border-r border-border bg-background font-geist overflow-y-auto">
        <SidebarHeader class="p-3">
            <!-- Account switcher -->
            <Link :href="role === 'creator' ? creatorDashboard.url() : role === 'admin' ? adminDashboard.url() : memberOverview.url()" class="flex items-center gap-2.5 rounded-md px-2 py-2 no-underline">
                <AppLogoIcon class="h-5 w-[18px] shrink-0" />
                <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-1.5">
                        <span class="truncate text-[15px] font-semibold tracking-tight text-foreground">
                            {{ creatorNav?.displayName ?? 'Novantt' }}
                        </span>
                        <span v-if="role === 'admin'" class="rounded bg-destructive/10 px-1.5 py-px font-mono text-[9px] font-semibold tracking-wider text-destructive">ADMIN</span>
                    </div>
                    <div v-if="role === 'admin'" class="text-[11px] text-muted-foreground">
                        {{ t('backend.admin.internalConsole') }}
                    </div>
                    <div v-else-if="role === 'user'" class="text-[11px] text-muted-foreground">
                        {{ t('backend.member.supporterAccount') }}
                    </div>
                </div>
            </Link>
        </SidebarHeader>

        <SidebarContent class="flex flex-1 flex-col gap-1 px-3">
            <!-- Primary navigation -->
            <nav class="flex flex-col gap-0.5">
                <Link
                    v-for="item in primaryNav"
                    :key="item.id"
                    :href="item.href"
                    class="flex items-center gap-2.5 rounded-md px-2 py-[7px] text-[13px] no-underline transition-colors"
                    :class="[
                        isCurrentUrl(item.href)
                            ? 'border border-border bg-card font-medium text-foreground shadow-[0_1px_1px_rgba(0,0,0,0.02)]'
                            : 'border border-transparent text-nv-ink2 hover:bg-nv-side-hover',
                    ]"
                >
                    <img
                        v-if="item.image"
                        :src="item.image"
                        :alt="item.label"
                        class="size-4 shrink-0 object-contain"
                    />
                    <component
                        v-else
                        :is="item.icon"
                        class="size-4 shrink-0"
                        :class="isCurrentUrl(item.href) ? 'text-nv-indigo' : 'text-muted-foreground'"
                        :stroke-width="1.6"
                    />
                    <span class="flex-1">{{ item.label }}</span>
                    <Badge
                        v-if="item.count != null"
                        variant="secondary"
                        class="h-5 min-w-5 px-1.5 font-mono text-[10px] tracking-wide"
                    >
                        {{ item.count.toLocaleString() }}
                    </Badge>
                </Link>
            </nav>

            <!-- Divider -->
            <div class="mx-1 my-3 h-px bg-border" />

            <!-- Secondary navigation -->
            <nav class="flex flex-col gap-0.5">
                <component
                    :is="item.external ? 'a' : Link"
                    v-for="item in secondaryNav"
                    :key="item.id"
                    :href="item.href"
                    :target="item.external ? '_blank' : undefined"
                    :rel="item.external ? 'noopener noreferrer' : undefined"
                    class="flex items-center gap-2.5 rounded-md border border-transparent px-2 py-[7px] text-[13px] text-nv-ink2 no-underline transition-colors hover:bg-nv-side-hover"
                >
                    <component :is="item.icon" class="size-4 shrink-0 text-muted-foreground" :stroke-width="1.6" />
                    <span>{{ item.label }}</span>
                </component>
            </nav>
        </SidebarContent>

        <SidebarFooter class="mt-auto flex flex-col gap-3 px-3 pb-3">
            <!-- Onboarding tour -->
            <div v-if="showTourButton" class="flex items-center rounded-md border border-border">
                <Link
                    :href="creatorDashboard.url({ query: { tour: '1' } })"
                    class="flex flex-1 items-center gap-2 px-2.5 py-[7px] text-[12px] text-muted-foreground no-underline transition-colors hover:bg-muted hover:text-foreground"
                >
                    <CirclePlay class="size-4 shrink-0" :stroke-width="1.6" />
                    {{ t('backend.sidebar.replayTour') }}
                </Link>
                <button
                    type="button"
                    class="flex shrink-0 items-center justify-center border-l border-border px-2 py-[7px] text-muted-foreground/50 transition-colors hover:bg-muted hover:text-foreground"
                    @click="showTourDismissConfirm = true"
                >
                    <X class="size-3.5" :stroke-width="1.6" />
                    <span class="sr-only">{{ t('backend.sidebar.dismissTour') }}</span>
                </button>
            </div>

            <!-- Theme toggle -->
            <div>
                <div class="mb-1.5 text-[11px] font-medium text-muted-foreground">{{ t('setup.theme') }}</div>
                <div class="flex rounded-md border border-border bg-muted p-0.5">
                    <button
                        v-for="th in themes"
                        :key="th.value"
                        class="flex flex-1 items-center justify-center gap-1 rounded py-1 text-[11px] transition-colors"
                        :class="appearance === th.value ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'"
                        @click="updateAppearance(th.value)"
                    >
                        <component :is="th.icon" class="size-3" :stroke-width="1.6" />
                        {{ th.label.value }}
                    </button>
                </div>
            </div>

            <!-- Language selector -->
            <div class="relative">
                <div class="mb-1.5 text-[11px] font-medium text-muted-foreground">{{ t('setup.language') }}</div>
                <button
                    class="flex h-8 w-full items-center gap-2 rounded-md border border-border bg-card px-2.5 text-[12px] text-foreground transition-colors hover:bg-muted"
                    @click="showLangMenu = !showLangMenu"
                >
                    <span class="text-sm leading-none">{{ currentLocaleData.flag }}</span>
                    <span class="flex-1 text-left">{{ currentLocaleData.label }}</span>
                    <Globe class="size-3.5 text-muted-foreground" :stroke-width="1.6" />
                </button>
                <div
                    v-if="showLangMenu"
                    class="absolute bottom-full left-0 mb-1 w-full overflow-hidden rounded-md border border-border bg-card py-1 shadow-lg"
                >
                    <button
                        v-for="l in locales"
                        :key="l.value"
                        class="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px] transition-colors hover:bg-muted"
                        :class="l.value === i18nLocale ? 'font-medium text-foreground' : 'text-muted-foreground'"
                        @click="changeLocale(l.value)"
                    >
                        <span class="text-sm leading-none">{{ l.flag }}</span>
                        {{ l.label }}
                    </button>
                </div>
            </div>
        </SidebarFooter>
    </Sidebar>

    <!-- Tour dismiss confirmation -->
    <Dialog v-model:open="showTourDismissConfirm">
        <DialogContent class="!max-w-[400px]">
            <DialogHeader>
                <div class="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-nv-warn-tint">
                    <AlertTriangle class="size-6 text-nv-warn" />
                </div>
                <DialogTitle class="text-center">{{ t('backend.sidebar.dismissTourConfirmTitle') }}</DialogTitle>
                <DialogDescription class="text-center">{{ t('backend.sidebar.dismissTourConfirmDescription') }}</DialogDescription>
            </DialogHeader>
            <DialogFooter class="gap-2 sm:justify-center">
                <Button variant="outline" size="sm" @click="showTourDismissConfirm = false">
                    {{ t('backend.sidebar.dismissTourConfirmCancel') }}
                </Button>
                <Button size="sm" variant="destructive" @click="confirmDismissTour">
                    {{ t('backend.sidebar.dismissTourConfirmAction') }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>

    <slot />
</template>
